import React from 'react';
import { Autocomplete, CircularProgress, Switch, TextField } from '@mui/material';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PopupBase from '../../../../../popup/base/PopupBase';
import CustomButton from '../../../../../components/buttons/CustomButton';
import { INSURANCE_PROVIDER_SUGGESTIONS } from '../../../../../consts/insuranceProviders';
import PlanChipsEditor from './PlanChipsEditor';
import styles from './ProviderFormPopup.module.css';

const NOTES_MAX = 500;

/** Interruptor de uma exigência da operadora (pedido médico, guia). */
function RequirementToggle({ Icon, title, hint, checked, disabled, onChange }) {
    return (
        <label className={styles.toggle}>
            <span className={styles.toggleIcon}><Icon fontSize="small" /></span>
            <span className={styles.toggleText}>
                <span className={styles.toggleTitle}>{title}</span>
                <span className={styles.hint}>{hint}</span>
            </span>
            <Switch
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                disabled={disabled}
                inputProps={{ 'aria-label': title }}
            />
        </label>
    );
}

/**
 * Criar ou editar uma operadora: nome (com sugestões), registro ANS, planos,
 * observações e, na edição, se está ativa. Validação e envio ficam na página
 * (CompanyInsurance); Enter num campo de uma linha envia.
 */
function ProviderFormPopup({ isEdit, formData, errors, saving, onFieldChange, onSubmit, onClose }) {
    const notesLength = (formData.notes || '').length;

    const content = (
        <form className={styles.content} onSubmit={onSubmit} noValidate>
            {errors.general && (
                <div className={styles.error} role="alert">{errors.general}</div>
            )}

            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Operadora</h4>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="provider-name">Nome *</label>
                    <Autocomplete
                        freeSolo
                        autoHighlight
                        options={INSURANCE_PROVIDER_SUGGESTIONS}
                        value={formData.name}
                        inputValue={formData.name}
                        onInputChange={(e, newValue) => onFieldChange('name', newValue || '')}
                        onChange={(e, newValue) => onFieldChange('name', newValue || '')}
                        disabled={saving}
                        size="small"
                        className={styles.input}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="provider-name"
                                autoFocus={!isEdit}
                                placeholder="Ex.: Unimed, Bradesco Saúde"
                                error={!!errors.name}
                                helperText={errors.name || 'Escolha da lista ou digite qualquer nome.'}
                                inputProps={{ ...params.inputProps, maxLength: 100 }}
                            />
                        )}
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="provider-ans">Registro ANS</label>
                    <TextField
                        id="provider-ans"
                        value={formData.ans_registry}
                        onChange={(e) => onFieldChange('ans_registry', e.target.value)}
                        disabled={saving}
                        size="small"
                        placeholder="Opcional"
                        error={!!errors.ans_registry}
                        helperText={errors.ans_registry || 'Número da operadora na ANS, se quiser guardar.'}
                        className={`${styles.input} ${styles.ansRegistry}`}
                        inputProps={{ maxLength: 20 }}
                    />
                </div>
            </section>

            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Planos</h4>
                <PlanChipsEditor
                    plans={formData.plans}
                    disabled={saving}
                    onChange={(plans) => onFieldChange('plans', plans)}
                />
            </section>

            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>O que a operadora exige</h4>
                <RequirementToggle
                    Icon={DescriptionOutlinedIcon}
                    title="Exige pedido médico"
                    hint="A agenda avisa quando o atendimento não tem pedido registrado."
                    checked={!!formData.requires_medical_request}
                    disabled={saving}
                    onChange={(checked) => onFieldChange('requires_medical_request', checked)}
                />
                <RequirementToggle
                    Icon={VerifiedOutlinedIcon}
                    title="Exige guia autorizada"
                    hint="A agenda avisa quando o atendimento não tem guia registrada."
                    checked={!!formData.requires_authorization}
                    disabled={saving}
                    onChange={(checked) => onFieldChange('requires_authorization', checked)}
                />
            </section>

            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Observações</h4>
                <div className={styles.field}>
                    <TextField
                        id="provider-notes"
                        value={formData.notes}
                        onChange={(e) => onFieldChange('notes', e.target.value)}
                        disabled={saving}
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        placeholder="Opcional. Telefone da central, regras de autorização, prazos"
                        error={!!errors.notes}
                        helperText={errors.notes || `${notesLength}/${NOTES_MAX}`}
                        className={`${styles.input} ${styles.counter}`}
                        inputProps={{ maxLength: NOTES_MAX, 'aria-label': 'Observações' }}
                    />
                </div>
                {isEdit && (
                    <label className={styles.toggle}>
                        <span className={styles.toggleIcon}><ToggleOnOutlinedIcon fontSize="small" /></span>
                        <span className={styles.toggleText}>
                            <span className={styles.toggleTitle}>Operadora ativa</span>
                            <span className={styles.hint}>Desligue para tirar a operadora das opções sem perder o histórico.</span>
                        </span>
                        <Switch
                            checked={!!formData.active}
                            onChange={(e) => onFieldChange('active', e.target.checked)}
                            disabled={saving}
                            inputProps={{ 'aria-label': 'Operadora ativa' }}
                        />
                    </label>
                )}
            </section>

            {/* Enter num campo de uma linha envia o formulário */}
            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
    );

    const footer = (
        <div className={styles.footer}>
            <CustomButton style="primary" variant="text" label="Cancelar" onClick={onClose} enabled={!saving} />
            <CustomButton
                style="main"
                variant="contained"
                label={isEdit ? 'Salvar' : 'Cadastrar operadora'}
                icon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={onSubmit}
                enabled={!saving}
            />
        </div>
    );

    return (
        <PopupBase
            Title={isEdit ? 'Editar operadora' : 'Nova operadora'}
            Content={content}
            Footer={footer}
            OnClose={onClose}
            maxWidth="sm"
        />
    );
}

export default ProviderFormPopup;
